import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { fmtUSD } from "../lib/format";
import { currentNepalMonth } from "../lib/partyBook";
import {
  Plus,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  X,
  Gift,
  Sparkles,
  Briefcase,
  Users,
  ArrowUpRight,
  ArrowDownLeft,
  TrendingUp,
  AlertTriangle,
  Coins,
  UsersRound,
  Wallet,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";

const BONUS_KINDS = [
  { v: "signup", l: "Signup" },
  { v: "survey", l: "Survey" },
  { v: "other", l: "Other" },
];
const BONUS_LABEL = Object.fromEntries(BONUS_KINDS.map((b) => [b.v, b.l]));
const SALARY_STRUCTURE_V2_START_MONTH = "2026-09";
const ROLLING_COIN_STOCK_START_MONTH = "2026-10";
const FIXED_STAFF_COIN_CUT_PCT = 16;
const STAFF_ROLES = ["CEO", "CTO", "COO", "CFO", "HR", "Marketing", "Extra"];
const DIRECT_NET_COIN_TEAM_KEYS = new Set(["tora", "bulls", "fin"]);
const teamKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const monthAdd = (ym, n) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const currentMonth = currentNepalMonth;
const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

const fmtNPR = (n) =>
  `NPR ${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const fmtPoints = (n, suffix = true) => {
  const value = Number(n || 0).toLocaleString("en-US", {
    maximumFractionDigits: 4,
  });
  return suffix ? `${value} pts` : value;
};

const num = (v) => (v === "" || v == null ? 0 : Number(v));
const fmtPct = (n) =>
  `${Number(n || 0).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}%`;

const EMPTY_FORM = {
  team_name: "",
  total_in: 0,
  total_out: 0,
  payment_gateway_pct: 0,
  dispute_amount: 0,
  total_coin_used: 0,
  coin_pct: 0,
  opening_stock: 0,
  total_loaded: 0,
  team_stock: 0,
  direct_net_coin: 0,
  team_pct: 0,
  manager_pct: 0,
  cf_pct: 0,
  npr_rate: 1,
  notes: "",
};

export default function Salaries() {
  const [month, setMonth] = useState(currentMonth());
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState({ others: [], staff_salaries: [] });
  const [loading, setLoading] = useState(true);

  // Sheet dialog
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // Bonus dialog
  const [bonusOpen, setBonusOpen] = useState(false);
  const [bonusForm, setBonusForm] = useState({
    sheet_id: "",
    staff_name: "",
    kind: "signup",
    amount_npr: "",
  });

  // Breakdown dialog
  const [bd, setBd] = useState(null);

  // Fixed leadership and staff salaries (September 2026 onward)
  const [staffEditorOpen, setStaffEditorOpen] = useState(false);
  const [staffEditingId, setStaffEditingId] = useState(null);
  const [staffForm, setStaffForm] = useState({
    role: "CEO",
    name: "",
    amount_npr: "",
  });

  const fixedStaffMode = month >= SALARY_STRUCTURE_V2_START_MONTH;
  const rollingCoinMode = month >= ROLLING_COIN_STOCK_START_MONTH;
  const directCoinMode =
    fixedStaffMode && DIRECT_NET_COIN_TEAM_KEYS.has(teamKey(form.team_name));

  const load = async () => {
    setLoading(true);
    try {
      const [list, sum, s] = await Promise.all([
        api.get("/salary-sheets", { params: { month } }),
        api.get("/salary-sheets/summary", { params: { month } }),
        api.get(`/salary-settings/${month}`),
      ]);
      setItems(list.data);
      setSummary(sum.data);
      setSettings(s.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [month]);

  const removeOther = async (id) => {
    try {
      await api.put(`/salary-settings/${month}`, {
        others: (settings.others || []).filter((o) => o.id !== id),
      });
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openAddStaff = () => {
    setStaffEditingId(null);
    setStaffForm({ role: "CEO", name: "", amount_npr: "" });
    setStaffEditorOpen(true);
  };

  const openEditStaff = (entry) => {
    setStaffEditingId(entry.id);
    setStaffForm({
      role: entry.role || "Extra",
      name: entry.name || "",
      amount_npr: entry.amount_npr || "",
    });
    setStaffEditorOpen(true);
  };

  const closeStaffEditor = () => {
    setStaffEditorOpen(false);
    setStaffEditingId(null);
  };

  const saveStaffSalary = async () => {
    const name = staffForm.name.trim();
    const amount = Number(staffForm.amount_npr);
    if (!name) return toast.error("Staff name required");
    if (!amount || amount <= 0) return toast.error("Salary must be positive");
    const entry = {
      ...(staffEditingId ? { id: staffEditingId } : {}),
      role: staffForm.role,
      name,
      amount_npr: amount,
    };
    const current = settings.staff_salaries || [];
    const next = staffEditingId
      ? current.map((item) => (item.id === staffEditingId ? entry : item))
      : [...current, entry];
    try {
      await api.put(`/salary-settings/${month}`, { staff_salaries: next });
      toast.success(staffEditingId ? "Staff salary updated" : "Staff salary added");
      closeStaffEditor();
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const removeStaffSalary = async (entry) => {
    if (!window.confirm(`Remove ${entry.name}'s salary?`)) return;
    try {
      await api.put(`/salary-settings/${month}`, {
        staff_salaries: (settings.staff_salaries || []).filter(
          (item) => item.id !== entry.id
        ),
      });
      toast.success("Staff salary removed");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  // sheet form math
  const computed = useMemo(() => {
    const gross = num(form.total_in) - num(form.total_out);
    const pg_cut = (num(form.payment_gateway_pct) / 100) * gross;
    const dispute = num(form.dispute_amount);
    const after_pg = gross - pg_cut;
    const after_dispute = after_pg - dispute;
    const net_coin_used = fixedStaffMode
      ? directCoinMode
        ? Math.max(num(form.direct_net_coin), 0)
        : Math.max(
            (rollingCoinMode ? num(form.opening_stock) : 0) +
              num(form.total_loaded) -
              num(form.team_stock),
            0
          )
      : (num(form.coin_pct) / 100) * num(form.total_coin_used);
    const coin_cut = fixedStaffMode
      ? (FIXED_STAFF_COIN_CUT_PCT / 100) * net_coin_used
      : net_coin_used;
    const net = after_dispute - coin_cut;
    const gross_margin = num(form.total_in) > 0 ? (gross / num(form.total_in)) * 100 : 0;
    const coin_gross_pct = gross > 0 ? (coin_cut / gross) * 100 : 0;
    const team_p = (num(form.team_pct) / 100) * net;
    const mgr_p = fixedStaffMode ? 0 : (num(form.manager_pct) / 100) * net;
    const cf_p = fixedStaffMode ? 0 : (num(form.cf_pct) / 100) * net;
    const company = net - team_p - mgr_p - cf_p;
    const total_usd = team_p + mgr_p + cf_p;
    const rate = num(form.npr_rate) || 1;
    return {
      gross,
      gross_margin,
      pg_cut,
      dispute,
      after_pg,
      after_dispute,
      net_coin_used,
      coin_cut,
      coin_gross_pct,
      net,
      team_p,
      mgr_p,
      cf_p,
      company,
      total_usd,
      total_npr: total_usd * rate,
      pct_total: fixedStaffMode
        ? num(form.team_pct)
        : num(form.team_pct) + num(form.manager_pct) + num(form.cf_pct),
    };
  }, [form, fixedStaffMode, rollingCoinMode, directCoinMode]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setOpen(true);
  };
  const openEdit = (sheet) => {
    setEditing(sheet);
    setForm({
      team_name: sheet.team_name,
      total_in: sheet.total_in || 0,
      total_out: sheet.total_out || 0,
      payment_gateway_pct: sheet.payment_gateway_pct || 0,
      dispute_amount: sheet.dispute_amount || sheet.computed?.dispute_amount || 0,
      total_coin_used: sheet.total_coin_used || 0,
      coin_pct: sheet.coin_pct || 0,
      opening_stock: sheet.opening_stock || 0,
      total_loaded: sheet.total_loaded || 0,
      team_stock: sheet.team_stock || 0,
      direct_net_coin: sheet.direct_net_coin || 0,
      team_pct: sheet.team_pct || 0,
      manager_pct: fixedStaffMode ? 0 : sheet.manager_pct || 0,
      cf_pct: fixedStaffMode ? 0 : sheet.cf_pct || 0,
      npr_rate: sheet.npr_rate || 1,
      notes: sheet.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.team_name.trim()) return toast.error("Team name required");
      const payload = {
        month,
        team_name: form.team_name.trim(),
        total_in: num(form.total_in),
        total_out: num(form.total_out),
        payment_gateway_pct: num(form.payment_gateway_pct),
        dispute_amount: num(form.dispute_amount),
        total_coin_used: num(form.total_coin_used),
        coin_pct: num(form.coin_pct),
        total_loaded: directCoinMode ? 0 : num(form.total_loaded),
        team_stock: directCoinMode ? 0 : num(form.team_stock),
        direct_net_coin: directCoinMode ? num(form.direct_net_coin) : 0,
        team_pct: num(form.team_pct),
        manager_pct: fixedStaffMode ? 0 : num(form.manager_pct),
        cf_pct: fixedStaffMode ? 0 : num(form.cf_pct),
        npr_rate: num(form.npr_rate) || 1,
        notes: form.notes || null,
      };
      if (editing) {
        await api.put(`/salary-sheets/${editing.id}`, payload);
        toast.success("Sheet updated");
      } else {
        await api.post("/salary-sheets", payload);
        toast.success("Sheet added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete sheet for "${s.team_name}"?`)) return;
    try {
      await api.delete(`/salary-sheets/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openAddBonus = () => {
    setBonusForm({
      sheet_id: items.length ? items[0].id : fixedStaffMode ? "" : "__others",
      staff_name: "",
      kind: "signup",
      amount_npr: "",
    });
    setBonusOpen(true);
  };
  const saveBonus = async () => {
    try {
      if (!bonusForm.sheet_id) return toast.error("Pick a team");
      if (!bonusForm.staff_name.trim())
        return toast.error("Staff name required");
      const amt = Number(bonusForm.amount_npr);
      if (!amt || amt <= 0) return toast.error("Amount must be positive");
      if (bonusForm.sheet_id === "__others") {
        // Save to monthly settings.others
        const next = [
          ...(settings.others || []),
          { name: bonusForm.staff_name.trim(), amount_npr: amt },
        ];
        await api.put(`/salary-settings/${month}`, { others: next });
        toast.success("Others entry added");
      } else {
        await api.post(`/salary-sheets/${bonusForm.sheet_id}/bonuses`, {
          staff_name: bonusForm.staff_name.trim(),
          kind: bonusForm.kind,
          amount_npr: amt,
        });
        toast.success("Bonus added");
      }
      setBonusOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };
  const removeBonus = async (sheetId, bonusId, name) => {
    if (!window.confirm(`Remove bonus for ${name}?`)) return;
    try {
      await api.delete(`/salary-sheets/${sheetId}/bonuses/${bonusId}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const t = summary?.totals || {};
  const inputT = summary?.input_totals || {};

  // Click handlers for breakdowns
  const showBd = (kind, title, rows, cols) =>
    setBd({ kind, title, rows: rows || [], cols });

  const grandTotalUsd = (t.total_payout_usd || 0); // sum of team + mgr + cf + bonus
  const grandTotalNpr = summary?.grand_total_payout_npr || 0; // includes others
  const editingUsesLedger = editing?.input_source === "daily_ledger";

  return (
    <Layout
      title="EM Salary Sheets"
      subtitle={
        fixedStaffMode
          ? "Team payouts plus fixed leadership and staff salaries"
          : "Per-team monthly salary, distribution % and bonus payouts"
      }
      testId="salaries-page"
      actions={
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          {fixedStaffMode && (
            <Button
              onClick={openAddStaff}
              variant="outline"
              className="h-9"
              data-testid="add-staff-salary-btn"
            >
              <UserPlus size={14} className="mr-1.5" /> Add salary
            </Button>
          )}
          <Button
            onClick={openAddBonus}
            variant="outline"
            className="h-9"
            data-testid="add-bonus-toplevel-btn"
          >
            <Gift size={14} className="mr-1.5" /> Add bonus
          </Button>
          <Button
            onClick={openCreate}
            className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="add-sheet-btn"
          >
            <Plus size={14} className="mr-1.5" /> Add team sheet
          </Button>
        </div>
      }
    >
      {/* Month nav */}
      <div className="stat-card p-3 mb-5 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setMonth(monthAdd(month, -1))}
          className="h-8 w-8 rounded-md border hairline bg-white hover:bg-zinc-50 flex items-center justify-center"
          data-testid="salaries-prev-month"
        >
          <ChevronLeft size={14} strokeWidth={1.7} />
        </button>
        <div
          className="font-display text-[15px] font-semibold tracking-tight px-2"
          data-testid="salaries-current-month"
        >
          {monthLabel(month)}
        </div>
        <button
          onClick={() => setMonth(monthAdd(month, 1))}
          className="h-8 w-8 rounded-md border hairline bg-white hover:bg-zinc-50 flex items-center justify-center"
          data-testid="salaries-next-month"
        >
          <ChevronRight size={14} strokeWidth={1.7} />
        </button>
        {month !== currentMonth() && (
          <button
            onClick={() => setMonth(currentMonth())}
            className="h-8 px-3 rounded-md text-[12.5px] border hairline bg-white hover:bg-zinc-50 ml-1"
            data-testid="salaries-this-month"
          >
            This month
          </button>
        )}
        <span className="ml-auto text-[11.5px] text-zinc-500 font-mono">
          {summary?.team_count || 0} team{summary?.team_count === 1 ? "" : "s"}
        </span>
      </div>

      {/* Row 1 — Input totals */}
      <div className="text-[10.5px] uppercase tracking-widest text-zinc-500 mb-2">
        Inputs & deductions (across all teams)
      </div>
      <div
        className={`grid grid-cols-2 gap-3 mb-5 ${
          fixedStaffMode
            ? rollingCoinMode
              ? "sm:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-9"
              : "sm:grid-cols-4 xl:grid-cols-8"
            : "sm:grid-cols-3 xl:grid-cols-5"
        }`}
      >
        <ClickStat
          label="Total in"
          icon={<ArrowDownLeft size={13} strokeWidth={1.7} />}
          accent="emerald"
          usd={inputT.total_in || 0}
          onClick={() =>
            showBd(
              "in",
              "Total In · per team",
              summary?.in_breakdown,
              ["team_name", "amount"]
            )
          }
          testId="top-totalin-card"
        />
        <ClickStat
          label="Total out"
          icon={<ArrowUpRight size={13} strokeWidth={1.7} />}
          accent="rose"
          usd={inputT.total_out || 0}
          onClick={() =>
            showBd(
              "out",
              "Total Out · per team",
              summary?.out_breakdown,
              ["team_name", "amount"]
            )
          }
          testId="top-totalout-card"
        />
        <ClickStat
          label="Gross profit"
          icon={<TrendingUp size={13} strokeWidth={1.7} />}
          accent="emerald"
          usd={t.gross_profit || 0}
          onClick={() =>
            showBd(
              "gross",
              "Gross profit · per team",
              summary?.gross_breakdown,
              ["team_name", "amount"]
            )
          }
          testId="top-gross-card"
        />
        <ClickStat
          label="Dispute amount"
          icon={<AlertTriangle size={13} strokeWidth={1.7} />}
          accent="rose"
          usd={t.dispute_amount || 0}
          onClick={() =>
            showBd(
              "dispute",
              "Dispute amount · per team",
              summary?.dispute_breakdown,
              ["team_name", "amount"]
            )
          }
          testId="top-dispute-card"
        />
        {fixedStaffMode ? (
          <>
            {rollingCoinMode && (
              <ClickStat
                label="Opening stock"
                icon={<Coins size={13} strokeWidth={1.7} />}
                accent="indigo"
                points={inputT.opening_stock || 0}
                sub="Previous month's closing stock"
                onClick={() =>
                  showBd(
                    "opening",
                    "Opening stock · previous month closing",
                    summary?.opening_breakdown,
                    ["team_name", "method", "source_month", "points"]
                  )
                }
                testId="top-opening-stock-card"
              />
            )}
            <ClickStat
              label="Loaded points"
              icon={<Coins size={13} strokeWidth={1.7} />}
              accent="sky"
              points={inputT.total_loaded || 0}
              onClick={() =>
                showBd(
                  "loaded",
                  "Loaded points · per team",
                  summary?.loaded_breakdown,
                  ["team_name", "method", "points"]
                )
              }
              testId="top-loaded-card"
            />
            <ClickStat
              label="Closing stock"
              icon={<Coins size={13} strokeWidth={1.7} />}
              accent="indigo"
              points={inputT.team_stock || 0}
              onClick={() =>
                showBd(
                  "stock",
                  "Closing stock · per team",
                  summary?.stock_breakdown,
                  ["team_name", "method", "points"]
                )
              }
              testId="top-stock-card"
            />
            <ClickStat
              label="Used points"
              icon={<Coins size={13} strokeWidth={1.7} />}
              accent="amber"
              points={inputT.net_coin_used || 0}
              sub={rollingCoinMode ? "Stock bridge or Direct Net · count only" : "Loaded − stock · count only, not USD"}
              onClick={() =>
                showBd(
                  "coin",
                  "Used points · per team",
                  summary?.coin_breakdown,
                  rollingCoinMode
                    ? ["team_name", "method", "opening", "loaded", "stock", "points"]
                    : ["team_name", "loaded", "stock", "points"]
                )
              }
              testId="top-coin-card"
            />
            <ClickStat
              label="Coin cut (16%)"
              icon={<Coins size={13} strokeWidth={1.7} />}
              accent="rose"
              usd={t.coin_cut || 0}
              sub={rollingCoinMode ? "16% of Used Coin · stock bridge or Direct Net" : "16% of Used Coin (Loaded − Stock)"}
              onClick={() =>
                showBd(
                  "coin-cut",
                  "Coin cut · 16% of Used Coin",
                  summary?.coin_breakdown,
                  ["team_name", "pct", "amount"]
                )
              }
              testId="top-coin-cut-card"
            />
          </>
        ) : (
          <ClickStat
            label="Coin used (USD cut)"
            icon={<Coins size={13} strokeWidth={1.7} />}
            accent="amber"
            usd={t.coin_cut || 0}
            sub={`raw ${(inputT.coin_used_raw || 0).toLocaleString("en-US", {
              maximumFractionDigits: 4,
            })}`}
            onClick={() =>
              showBd(
                "coin",
                "Coin cut · per team (after %)",
                summary?.coin_breakdown,
                ["team_name", "pct", "raw", "amount"]
              )
            }
            testId="top-coin-card"
          />
        )}
      </div>

      {/* Row 2 — Payout totals */}
      <div className="text-[10.5px] uppercase tracking-widest text-zinc-500 mb-2">
        Payouts (clickable for per-team / per-staff breakdown)
      </div>
      <div
        className={`grid grid-cols-2 gap-3 mb-5 ${
          fixedStaffMode ? "sm:grid-cols-4" : "sm:grid-cols-3 lg:grid-cols-6"
        }`}
      >
        <ClickStat
          label="Teams"
          icon={<UsersRound size={13} strokeWidth={1.7} />}
          accent="indigo"
          usd={t.team_payout_usd || 0}
          npr={t.team_payout_npr || 0}
          onClick={() =>
            showBd(
              "team",
              "Team payouts · per team",
              summary?.team_breakdown,
              ["team_name", "pct", "usd", "npr"]
            )
          }
          testId="top-team-card"
        />
        {!fixedStaffMode && (
          <ClickStat
            label="Managers"
            icon={<Briefcase size={13} strokeWidth={1.7} />}
            accent="indigo"
            usd={t.manager_payout_usd || 0}
            npr={t.manager_payout_npr || 0}
            onClick={() =>
              showBd(
                "manager",
                "Manager payouts · per team",
                (summary?.manager_breakdown || []).filter(
                  (r) => r.usd > 0 || r.pct > 0
                ),
                ["team_name", "pct", "usd", "npr"]
              )
            }
            testId="top-manager-card"
          />
        )}
        {!fixedStaffMode && (
          <ClickStat
            label="CF"
            icon={<Users size={13} strokeWidth={1.7} />}
            accent="sky"
            usd={t.cf_payout_usd || 0}
            npr={t.cf_payout_npr || 0}
            onClick={() =>
              showBd(
                "cf",
                "CF payouts · per team",
                (summary?.cf_breakdown || []).filter(
                  (r) => r.usd > 0 || r.pct > 0
                ),
                ["team_name", "pct", "usd", "npr"]
              )
            }
            testId="top-cf-card"
          />
        )}
        {fixedStaffMode && (
          <ClickStat
            label="Staff salaries"
            icon={<Briefcase size={13} strokeWidth={1.7} />}
            accent="sky"
            usd={null}
            npr={summary?.staff_salaries_total_npr || 0}
            onClick={() =>
              showBd(
                "staff",
                "Leadership & staff salaries",
                summary?.staff_salary_breakdown,
                ["role", "name", "amount_npr"]
              )
            }
            testId="top-staff-salaries-card"
          />
        )}
        <ClickStat
          label="Bonuses"
          icon={<Gift size={13} strokeWidth={1.7} />}
          accent="amber"
          usd={null}
          npr={t.bonus_total_npr || 0}
          onClick={() =>
            showBd(
              "bonus",
              "Bonuses · per staff",
              summary?.bonus_breakdown,
              ["staff_name", "team_name", "kind", "npr"]
            )
          }
          testId="top-bonus-card"
        />
        {!fixedStaffMode && (
          <ClickStat
            label="Others"
            icon={<Sparkles size={13} strokeWidth={1.7} />}
            accent="amber"
            usd={null}
            npr={summary?.others_total_npr || 0}
            onClick={() =>
              showBd(
                "others",
                "Others · custom NPR salaries",
                (settings.others || []).map((o) => ({
                  id: o.id,
                  name: o.name,
                  amount_npr: o.amount_npr,
                })),
                ["name", "amount_npr"]
              )
            }
            testId="top-others-card"
          />
        )}
        <ClickStat
          label="Grand total to pay"
          icon={<Wallet size={13} strokeWidth={1.7} />}
          accent="rose"
          usd={grandTotalUsd}
          npr={grandTotalNpr}
          highlight
          onClick={() =>
            showBd(
              "grand",
              "Grand total to pay · components",
              [
                {
                  label: "Teams",
                  usd: t.team_payout_usd || 0,
                  npr: t.team_payout_npr || 0,
                },
                ...(!fixedStaffMode ? [{
                  label: "Managers",
                  usd: t.manager_payout_usd || 0,
                  npr: t.manager_payout_npr || 0,
                }, {
                  label: "CF",
                  usd: t.cf_payout_usd || 0,
                  npr: t.cf_payout_npr || 0,
                }] : []),
                ...(fixedStaffMode ? [{
                  label: "Staff salaries",
                  usd: null,
                  npr: summary?.staff_salaries_total_npr || 0,
                }] : []),
                {
                  label: "Bonuses",
                  usd: t.bonus_total_usd || 0,
                  npr: t.bonus_total_npr || 0,
                },
                ...(!fixedStaffMode ? [{
                  label: "Others",
                  usd: null,
                  npr: summary?.others_total_npr || 0,
                }] : []),
              ],
              ["label", "usd", "npr"]
            )
          }
          testId="top-grand-card"
        />
      </div>

      {fixedStaffMode && (
        <section className="stat-card overflow-hidden mb-5" data-testid="staff-salaries-section">
          <div className="p-4 border-b hairline flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-display font-bold tracking-tight">
                Leadership & staff salaries
              </div>
              <div className="text-[11.5px] text-zinc-500 mt-0.5">
                Fixed monthly NPR salaries · separate from team profit percentages
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[13px] font-medium">
                {fmtNPR(summary?.staff_salaries_total_npr || 0)}
              </span>
              <Button
                type="button"
                variant="outline"
                className="h-8"
                onClick={openAddStaff}
                data-testid="staff-section-add-btn"
              >
                <Plus size={13} className="mr-1.5" /> Add salary
              </Button>
            </div>
          </div>

          {staffEditorOpen && (
            <div className="p-4 border-b hairline bg-zinc-50/60" data-testid="staff-salary-editor">
              <div className="grid gap-3 sm:grid-cols-[160px_minmax(0,1fr)_190px_auto] sm:items-end">
                <div className="space-y-1.5">
                  <Label htmlFor="staff-role">Role</Label>
                  <Select
                    value={staffForm.role}
                    onValueChange={(role) => setStaffForm({ ...staffForm, role })}
                  >
                    <SelectTrigger id="staff-role" data-testid="staff-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STAFF_ROLES.map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="staff-name">Name</Label>
                  <Input
                    id="staff-name"
                    value={staffForm.name}
                    onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })}
                    placeholder="Person name"
                    autoFocus
                    data-testid="staff-name-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="staff-amount">Monthly salary (NPR)</Label>
                  <Input
                    id="staff-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={staffForm.amount_npr}
                    onChange={(e) =>
                      setStaffForm({ ...staffForm, amount_npr: e.target.value })
                    }
                    placeholder="0"
                    data-testid="staff-amount-input"
                  />
                </div>
                <div className="flex gap-2 sm:pb-0.5">
                  <Button type="button" variant="outline" className="h-9" onClick={closeStaffEditor}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
                    onClick={saveStaffSalary}
                    data-testid="save-staff-salary-btn"
                  >
                    {staffEditingId ? "Save" : "Add"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {(settings.staff_salaries || []).length === 0 ? (
            <div className="px-4 py-8 text-center">
              <UsersRound size={20} strokeWidth={1.5} className="mx-auto text-zinc-400" />
              <div className="text-[13px] text-zinc-600 mt-2">No staff salaries added for this month.</div>
              <div className="text-[11.5px] text-zinc-400 mt-0.5">
                Add CEO, CTO, COO, CFO, HR, Marketing or any extra person.
              </div>
            </div>
          ) : (
            <div>
              <div className="hidden sm:grid sm:grid-cols-[120px_minmax(0,1fr)_180px_72px] px-4 py-2.5 text-[10.5px] uppercase tracking-widest text-zinc-500 bg-zinc-50/50 border-b hairline">
                <div>Role</div>
                <div>Name</div>
                <div className="text-right">Monthly NPR</div>
                <div className="text-right">Actions</div>
              </div>
              <div className="divide-y hairline">
                {(settings.staff_salaries || []).map((entry) => (
                  <div
                    key={entry.id}
                    className="grid grid-cols-[80px_minmax(0,1fr)_72px] sm:grid-cols-[120px_minmax(0,1fr)_180px_72px] items-center gap-y-1 px-4 py-3 row-hover"
                    data-testid={`staff-salary-row-${entry.id}`}
                  >
                    <div>
                      <span className="inline-flex rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10.5px] font-medium text-zinc-600">
                        {entry.role}
                      </span>
                    </div>
                    <div className="min-w-0 truncate text-[13px] font-medium">{entry.name}</div>
                    <div className="col-span-2 font-mono text-[12.5px] text-zinc-700 sm:col-span-1 sm:text-right">
                      {fmtNPR(entry.amount_npr)}
                    </div>
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => openEditStaff(entry)}
                        className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600 active:scale-[0.97]"
                        aria-label={`Edit ${entry.name}`}
                      >
                        <Pencil size={14} strokeWidth={1.6} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeStaffSalary(entry)}
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-600 active:scale-[0.97]"
                        aria-label={`Remove ${entry.name}`}
                      >
                        <Trash2 size={14} strokeWidth={1.6} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Teams table (no bonus column — bonuses are separate) */}
      <div className="stat-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                <th className="py-3 px-4 font-medium">Team</th>
                <th className="py-3 px-4 font-medium text-right">In</th>
                <th className="py-3 px-4 font-medium text-right">Out</th>
                <th className="py-3 px-4 font-medium text-right">Gross</th>
                <th className="py-3 px-4 font-medium text-right">Gross margin</th>
                <th className="py-3 px-4 font-medium text-right">PG %</th>
                <th className="py-3 px-4 font-medium text-right">Dispute</th>
                {fixedStaffMode ? (
                  <>
                    {rollingCoinMode && (
                      <th className="py-3 px-4 font-medium text-right">Opening pts</th>
                    )}
                    <th className="py-3 px-4 font-medium text-right">Loaded pts</th>
                    <th className="py-3 px-4 font-medium text-right">Closing pts</th>
                    <th className="py-3 px-4 font-medium text-right">Used pts</th>
                    <th className="py-3 px-4 font-medium text-right">Coin cut</th>
                  </>
                ) : (
                  <>
                    <th className="py-3 px-4 font-medium text-right">Coin %</th>
                    <th className="py-3 px-4 font-medium text-right">Coin / gross</th>
                  </>
                )}
                <th className="py-3 px-4 font-medium text-right">Net</th>
                <th className="py-3 px-4 font-medium text-right">Team</th>
                {!fixedStaffMode && (
                  <th className="py-3 px-4 font-medium text-right">Mgr</th>
                )}
                {!fixedStaffMode && (
                  <th className="py-3 px-4 font-medium text-right">CF</th>
                )}
                <th className="py-3 px-4 font-medium text-right">Bonuses</th>
                <th className="py-3 px-4 font-medium text-right">Team total NPR</th>
                <th className="py-3 px-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr>
                  <td colSpan={rollingCoinMode ? 17 : 16} className="py-8 text-center text-zinc-500">
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={rollingCoinMode ? 17 : 16} className="py-12 text-center text-zinc-500">
                    <ClipboardList
                      size={20}
                      strokeWidth={1.5}
                      className="mx-auto mb-2 text-zinc-400"
                    />
                    No team sheets for {monthLabel(month)} yet.
                  </td>
                </tr>
              ) : (
                items.map((s) => {
                  const c = s.computed || {};
                  return (
                    <tr
                      key={s.id}
                      className="row-hover"
                      data-testid={`sheet-row-${s.id}`}
                    >
                      <td className="py-3 px-4 font-medium">
                        <div>{s.team_name}</div>
                        {c.coin_entry_mode === "direct" && (
                          <div className="mt-0.5 text-[10.5px] text-amber-700">
                            Direct Net Coin
                          </div>
                        )}
                        {s.input_source === "daily_ledger" && (
                          <div className="mt-0.5 text-[10.5px] text-sky-700">
                            Daily ledger · {s.ledger_days || 0} day{s.ledger_days === 1 ? "" : "s"}
                          </div>
                        )}
                        {s.bonuses?.length > 0 && (
                          <div className="text-[10.5px] text-amber-700 mt-0.5">
                            {s.bonuses.length} bonus
                            {s.bonuses.length === 1 ? "" : "es"}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 font-mono text-right">
                        {fmtUSD(s.total_in)}
                      </td>
                      <td className="py-3 px-4 font-mono text-right">
                        {fmtUSD(s.total_out)}
                      </td>
                      <td className="py-3 px-4 font-mono text-right">
                        {fmtUSD(c.gross_profit || 0)}
                      </td>
                      <td className="py-3 px-4 font-mono text-right text-zinc-500">
                        {fmtPct(c.gross_profit_margin_pct)}
                      </td>
                      <td className="py-3 px-4 font-mono text-right text-zinc-500">
                        {s.payment_gateway_pct}% · {fmtUSD(c.pg_cut || 0)}
                      </td>
                      <td className="py-3 px-4 font-mono text-right text-rose-700">
                        {fmtUSD(c.dispute_amount || s.dispute_amount || 0)}
                      </td>
                      {fixedStaffMode ? (
                        <>
                          {rollingCoinMode && (
                            <td className="py-3 px-4 font-mono text-right text-indigo-700">
                              {c.coin_entry_mode === "direct"
                                ? "—"
                                : fmtPoints(c.opening_stock || 0, false)}
                            </td>
                          )}
                          <td className="py-3 px-4 font-mono text-right text-zinc-500">
                            {c.coin_entry_mode === "direct"
                              ? "—"
                              : fmtPoints(s.total_loaded || 0, false)}
                          </td>
                          <td className="py-3 px-4 font-mono text-right text-zinc-500">
                            {c.coin_entry_mode === "direct"
                              ? "—"
                              : fmtPoints(s.team_stock || 0, false)}
                          </td>
                          <td className="py-3 px-4 font-mono text-right font-medium text-amber-700">
                            {fmtPoints(c.net_coin_used || 0, false)}
                          </td>
                          <td className="py-3 px-4 font-mono text-right font-medium text-rose-700">
                            {fmtUSD(c.coin_cut || 0)}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-3 px-4 font-mono text-right text-zinc-500">
                            {s.coin_pct}% · {fmtUSD(c.coin_cut || 0)}
                          </td>
                          <td className="py-3 px-4 font-mono text-right text-zinc-500">
                            {fmtPct(c.coin_consumption_pct_of_gross)}
                          </td>
                        </>
                      )}
                      <td className="py-3 px-4 font-mono text-right font-semibold text-emerald-700">
                        {fmtUSD(c.net_profit || 0)}
                      </td>
                      <td className="py-3 px-4 font-mono text-right">
                        <div>{s.team_pct}%</div>
                        <div className="text-[11px] text-zinc-500">
                          {fmtUSD(c.team_payout_usd || 0)}
                        </div>
                      </td>
                      {!fixedStaffMode && (
                        <td className="py-3 px-4 font-mono text-right">
                          <div>{s.manager_pct}%</div>
                          <div className="text-[11px] text-zinc-500">
                            {fmtUSD(c.manager_payout_usd || 0)}
                          </div>
                        </td>
                      )}
                      {!fixedStaffMode && (
                        <td className="py-3 px-4 font-mono text-right">
                          <div>{s.cf_pct}%</div>
                          <div className="text-[11px] text-zinc-500">
                            {fmtUSD(c.cf_payout_usd || 0)}
                          </div>
                        </td>
                      )}
                      <td className="py-3 px-4 font-mono text-right">
                        {s.bonuses?.length ? (
                          <BonusCell
                            sheet={s}
                            onRemove={removeBonus}
                          />
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-mono text-right text-rose-700">
                        {fmtNPR(c.team_payout_npr || 0)}
                        <div className="text-[10.5px] text-zinc-500">
                          @ {s.npr_rate} (team % only)
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEdit(s)}
                            className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
                            data-testid={`edit-sheet-${s.id}`}
                          >
                            <Pencil size={14} strokeWidth={1.6} />
                          </button>
                          <button
                            onClick={() => remove(s)}
                            className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                            data-testid={`delete-sheet-${s.id}`}
                          >
                            <Trash2 size={14} strokeWidth={1.6} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sheet dialog (no more bonuses inside) */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="sheet-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit team sheet" : "Add team sheet"}
            </DialogTitle>
            <DialogDescription>
              {monthLabel(month)} · all amounts in USD unless noted. Bonuses
              are added separately.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">
                Inputs
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5 col-span-2 sm:col-span-3">
                  <Label>Team name *</Label>
                  <Input
                    value={form.team_name}
                    onChange={(e) =>
                      setForm({ ...form, team_name: e.target.value })
                    }
                    placeholder="e.g. Team A"
                    disabled={editingUsesLedger}
                    data-testid="sheet-team-input"
                  />
                </div>
                {editingUsesLedger && (
                  <div className="col-span-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-[11.5px] text-sky-800 sm:col-span-3">
                    In, Out and Dispute are calculated from {editing.ledger_days || 0} daily ledger rows. Edit those rows above; {fixedStaffMode ? "Loaded and Stock" : "Coin Used"} remain manual here.
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>Total in (USD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.total_in}
                    disabled={editingUsesLedger}
                    onChange={(e) =>
                      setForm({ ...form, total_in: e.target.value })
                    }
                    data-testid="sheet-totalin-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Total out (USD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.total_out}
                    disabled={editingUsesLedger}
                    onChange={(e) =>
                      setForm({ ...form, total_out: e.target.value })
                    }
                    data-testid="sheet-totalout-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Payment gateway %</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.payment_gateway_pct}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        payment_gateway_pct: e.target.value,
                      })
                    }
                    data-testid="sheet-pg-pct-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Dispute amount (USD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.dispute_amount}
                    disabled={editingUsesLedger}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        dispute_amount: e.target.value,
                      })
                    }
                    data-testid="sheet-dispute-amount-input"
                  />
                </div>
                {fixedStaffMode ? (
                  <>
                    {directCoinMode ? (
                      <div className="space-y-1.5">
                        <Label>Direct net coin points</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.0001"
                          value={form.direct_net_coin}
                          onChange={(e) =>
                            setForm({ ...form, direct_net_coin: e.target.value })
                          }
                          data-testid="sheet-direct-net-coin-input"
                        />
                        <div className="text-[10.5px] text-zinc-500">
                          Used Coin is entered directly for this Ledger EMP team.
                        </div>
                      </div>
                    ) : (
                      <>
                        {rollingCoinMode && (
                          <div className="space-y-1.5">
                            <Label>Opening stock points</Label>
                            <Input
                              value={fmtPoints(form.opening_stock)}
                              readOnly
                              tabIndex={-1}
                              className="font-mono bg-indigo-50/60 text-indigo-800"
                              data-testid="sheet-opening-stock-output"
                            />
                            <div className="text-[10.5px] text-zinc-500">
                              {monthLabel(monthAdd(month, -1))} closing stock · auto-carried
                            </div>
                          </div>
                        )}
                        <div className="space-y-1.5">
                          <Label>Loaded points</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.0001"
                            value={form.total_loaded}
                            onChange={(e) =>
                              setForm({ ...form, total_loaded: e.target.value })
                            }
                            data-testid="sheet-total-loaded-input"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Closing stock points</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.0001"
                            value={form.team_stock}
                            onChange={(e) =>
                              setForm({ ...form, team_stock: e.target.value })
                            }
                            data-testid="sheet-team-stock-input"
                          />
                        </div>
                      </>
                    )}
                    <div className="space-y-1.5">
                      <Label>Used points</Label>
                      <Input
                        value={fmtPoints(computed.net_coin_used)}
                        readOnly
                        tabIndex={-1}
                        className="font-mono bg-amber-50/60 text-amber-800"
                        data-testid="sheet-net-coin-output"
                      />
                      <div className="text-[10.5px] text-zinc-500">
                        {directCoinMode
                          ? "Direct Net Coin · no opening, loaded or closing stock"
                          : rollingCoinMode
                          ? "Opening stock + loaded points − closing stock"
                          : "Loaded − stock · inventory count"}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Coin cut (16% of Used Coin)</Label>
                      <Input
                        value={fmtUSD(computed.coin_cut)}
                        readOnly
                        tabIndex={-1}
                        className="font-mono bg-rose-50/60 text-rose-800"
                        data-testid="sheet-coin-cut-output"
                      />
                      <div className="text-[10.5px] text-zinc-500">
                        {directCoinMode
                          ? "16% × max(Direct Net Coin, 0)"
                          : rollingCoinMode
                          ? "16% × max(Opening + Loaded − Closing, 0)"
                          : "16% × max(Loaded − Stock, 0)"}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label>Total coin used</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        value={form.total_coin_used}
                        onChange={(e) =>
                          setForm({ ...form, total_coin_used: e.target.value })
                        }
                        data-testid="sheet-coinused-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Coin %</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={form.coin_pct}
                        onChange={(e) =>
                          setForm({ ...form, coin_pct: e.target.value })
                        }
                        data-testid="sheet-coinpct-input"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">
                {fixedStaffMode ? "Team distribution" : "Distribution % (of net profit)"}
              </div>
              <div className={`grid grid-cols-2 gap-3 ${fixedStaffMode ? "sm:max-w-xl" : "sm:grid-cols-4"}`}>
                <div className="space-y-1.5">
                  <Label>Team %</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.team_pct}
                    onChange={(e) =>
                      setForm({ ...form, team_pct: e.target.value })
                    }
                    data-testid="sheet-teampct-input"
                  />
                </div>
                {!fixedStaffMode && (
                  <div className="space-y-1.5">
                    <Label>Manager %</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.manager_pct}
                      onChange={(e) =>
                        setForm({ ...form, manager_pct: e.target.value })
                      }
                      data-testid="sheet-mgrpct-input"
                    />
                  </div>
                )}
                {!fixedStaffMode && (
                  <div className="space-y-1.5">
                    <Label>CF %</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.cf_pct}
                      onChange={(e) =>
                        setForm({ ...form, cf_pct: e.target.value })
                      }
                      data-testid="sheet-cfpct-input"
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label>NPR rate (per USD)</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    value={form.npr_rate}
                    onChange={(e) =>
                      setForm({ ...form, npr_rate: e.target.value })
                    }
                    data-testid="sheet-nprrate-input"
                  />
                </div>
              </div>
              {computed.pct_total > 100 && (
                <div className="text-[11.5px] text-rose-700 mt-2">
                  Sum of % is {computed.pct_total}% (over 100%)
                </div>
              )}
            </div>

            <div className="rounded-md border hairline bg-zinc-50/50 p-3">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">
                Live preview (excludes bonuses)
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px]">
                <Stat label="Gross" value={fmtUSD(computed.gross)} />
                <Stat label="Gross margin" value={fmtPct(computed.gross_margin)} />
                <Stat label="PG cut" value={fmtUSD(computed.pg_cut)} />
                <Stat label="Dispute" value={fmtUSD(computed.dispute)} />
                <Stat
                  label={fixedStaffMode ? "Used points" : "Coin cut"}
                  value={
                    fixedStaffMode
                      ? fmtPoints(computed.net_coin_used)
                      : fmtUSD(computed.coin_cut)
                  }
                />
                {fixedStaffMode && (
                  <Stat
                    label="Coin cut (16%)"
                    value={fmtUSD(computed.coin_cut)}
                  />
                )}
                {!fixedStaffMode && (
                  <Stat
                    label="Coin / gross"
                    value={fmtPct(computed.coin_gross_pct)}
                  />
                )}
                <Stat
                  label="Net profit"
                  value={fmtUSD(computed.net)}
                  accent="emerald"
                />
                <Stat label="Team payout" value={fmtUSD(computed.team_p)} />
                {!fixedStaffMode && (
                  <Stat
                    label="Manager payout"
                    value={fmtUSD(computed.mgr_p)}
                  />
                )}
                {!fixedStaffMode && (
                  <Stat label="CF payout" value={fmtUSD(computed.cf_p)} />
                )}
                <Stat
                  label="Company keeps"
                  value={fmtUSD(computed.company)}
                  accent="emerald"
                />
                <Stat
                  label={fixedStaffMode ? "Team payout USD" : "Team total payout USD"}
                  value={fmtUSD(computed.total_usd)}
                  accent="rose"
                />
                <Stat
                  label="Team total payout NPR"
                  value={fmtNPR(computed.total_npr)}
                  accent="rose"
                  span={3}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional"
                data-testid="sheet-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-sheet-btn"
            >
              {editing ? "Save changes" : "Add sheet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bonus dialog */}
      <Dialog open={bonusOpen} onOpenChange={setBonusOpen}>
        <DialogContent data-testid="bonus-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add bonus</DialogTitle>
            <DialogDescription>
              Pick a team sheet and the staff member receiving the bonus
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Team *</Label>
              <Select
                value={bonusForm.sheet_id}
                onValueChange={(v) =>
                  setBonusForm({ ...bonusForm, sheet_id: v })
                }
              >
                <SelectTrigger data-testid="bonus-team-select">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {items.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.team_name}
                    </SelectItem>
                  ))}
                  {!fixedStaffMode && (
                    <SelectItem value="__others">
                      Others (no team · custom NPR)
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{bonusForm.sheet_id === "__others" ? "Name / role *" : "Staff name *"}</Label>
              <Input
                value={bonusForm.staff_name}
                onChange={(e) =>
                  setBonusForm({ ...bonusForm, staff_name: e.target.value })
                }
                placeholder={
                  bonusForm.sheet_id === "__others" ? "e.g. Office boy" : "e.g. Ravi"
                }
                data-testid="bonus-staff-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {bonusForm.sheet_id !== "__others" && (
                <div className="space-y-1.5">
                  <Label>Kind</Label>
                  <Select
                    value={bonusForm.kind}
                    onValueChange={(v) =>
                      setBonusForm({ ...bonusForm, kind: v })
                    }
                  >
                    <SelectTrigger data-testid="bonus-kind-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BONUS_KINDS.map((k) => (
                        <SelectItem key={k.v} value={k.v}>
                          {k.l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div
                className={`space-y-1.5 ${
                  bonusForm.sheet_id === "__others" ? "col-span-2" : ""
                }`}
              >
                <Label>Amount (NPR) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={bonusForm.amount_npr}
                  onChange={(e) =>
                    setBonusForm({ ...bonusForm, amount_npr: e.target.value })
                  }
                  data-testid="bonus-amount-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBonusOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveBonus}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-bonus-btn"
            >
              <Gift size={13} className="mr-1" /> Add bonus
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Breakdown dialog */}
      <Dialog open={!!bd} onOpenChange={(o) => !o && setBd(null)}>
        <DialogContent
          className="max-w-lg"
          data-testid={`breakdown-${bd?.kind || "x"}`}
        >
          <DialogHeader>
            <DialogTitle className="font-display">{bd?.title}</DialogTitle>
          </DialogHeader>
          {(bd?.rows || []).length === 0 ? (
            <div className="text-[13px] text-zinc-500 py-4 text-center">
              No entries.
            </div>
          ) : (
            <div className="rounded-md border hairline overflow-hidden">
              <table className="min-w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                    {bd.cols.map((c) => (
                      <th
                        key={c}
                        className={`py-2 px-3 ${
                          [
                            "amount",
                            "amount_npr",
                            "usd",
                            "npr",
                            "raw",
                            "pct",
                            "opening",
                            "loaded",
                            "stock",
                            "points",
                          ].includes(c)
                            ? "text-right"
                            : ""
                        }`}
                      >
                        {labelFor(c)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y hairline">
                  {bd.rows.map((r, i) => (
                    <tr key={i}>
                      {bd.cols.map((c) => (
                        <td
                          key={c}
                          className={`py-2 px-3 ${
                            [
                              "amount",
                              "amount_npr",
                              "usd",
                              "npr",
                              "raw",
                              "pct",
                              "opening",
                              "loaded",
                              "stock",
                              "points",
                            ].includes(c)
                              ? "font-mono text-right"
                              : ""
                          } ${
                            c === "amount_npr" || c === "npr"
                              ? "text-rose-700"
                              : ""
                          }`}
                        >
                          {renderCell(c, r[c], r)}
                        </td>
                      ))}
                      {bd.kind === "others" && (
                        <td className="py-2 px-3 text-right w-10">
                          <button
                            onClick={async () => {
                              await removeOther(r.id);
                              setBd(null);
                            }}
                            className="text-rose-600 hover:bg-rose-50 p-1 rounded"
                            data-testid={`delete-other-${r.id}`}
                          >
                            <X size={11} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-50/60">
                    {bd.cols.map((c, i) => {
                      if (i === 0)
                        return (
                          <td
                            key={c}
                            className="py-2 px-3 text-[10.5px] uppercase tracking-widest text-zinc-500"
                          >
                            Total
                          </td>
                        );
                      if (
                        [
                          "amount",
                          "amount_npr",
                          "usd",
                          "npr",
                          "opening",
                          "loaded",
                          "stock",
                          "points",
                        ].includes(c)
                      ) {
                        const sum = bd.rows.reduce(
                          (s, r) => s + Number(r[c] || 0),
                          0
                        );
                        const isNpr = c === "amount_npr" || c === "npr";
                        const isPoints = ["opening", "loaded", "stock", "points"].includes(c);
                        return (
                          <td
                            key={c}
                            className={`py-2 px-3 font-mono text-right font-semibold ${
                              isNpr ? "text-rose-700" : ""
                            }`}
                          >
                            {isNpr
                              ? fmtNPR(sum)
                              : isPoints
                                ? fmtPoints(sum)
                                : fmtUSD(sum)}
                          </td>
                        );
                      }
                      return <td key={c}></td>;
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function labelFor(col) {
  return (
    {
      team_name: "Team",
      staff_name: "Staff",
      kind: "Kind",
      pct: "%",
      raw: "Raw",
      amount: "USD",
      amount_npr: "NPR",
      usd: "USD",
      npr: "NPR",
      label: "Component",
      name: "Name",
      role: "Role",
      method: "Method",
      source_month: "From",
      opening: "Opening",
      loaded: "Loaded",
      stock: "Closing",
      points: "Points",
    }[col] || col
  );
}

function renderCell(col, val, _row) {
  if (val == null) return "—";
  if (col === "kind") return BONUS_LABEL[val] || val;
  if (col === "pct") return `${val}%`;
  if (col === "raw")
    return Number(val).toLocaleString("en-US", { maximumFractionDigits: 4 });
  if (col === "amount" || col === "usd") return fmtUSD(val);
  if (col === "amount_npr" || col === "npr") return fmtNPR(val);
  if (["opening", "loaded", "stock", "points"].includes(col)) return fmtPoints(val);
  return val;
}

function BonusCell({ sheet, onRemove }) {
  const [open, setOpen] = useState(false);
  const totalNpr = (sheet.bonuses || []).reduce(
    (s, b) => s + (b.amount_npr || 0),
    0
  );
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-amber-700 hover:underline"
        data-testid={`bonus-cell-${sheet.id}`}
      >
        {fmtNPR(totalNpr)}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              Bonuses · {sheet.team_name}
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-md border hairline overflow-hidden">
            <table className="min-w-full text-[12.5px]">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                  <th className="py-2 px-3">Staff</th>
                  <th className="py-2 px-3">Kind</th>
                  <th className="py-2 px-3 text-right">NPR</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y hairline">
                {(sheet.bonuses || []).map((b) => (
                  <tr key={b.id}>
                    <td className="py-2 px-3">{b.staff_name}</td>
                    <td className="py-2 px-3 text-zinc-600">
                      {BONUS_LABEL[b.kind] || b.kind}
                    </td>
                    <td className="py-2 px-3 font-mono text-right text-amber-700">
                      {fmtNPR(b.amount_npr)}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <button
                        onClick={() => onRemove(sheet.id, b.id, b.staff_name)}
                        className="text-rose-600 hover:bg-rose-50 p-1 rounded"
                      >
                        <X size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ClickStat({
  label,
  icon,
  accent,
  usd,
  points,
  npr,
  sub,
  usdOverride,
  highlight,
  onClick,
  testId,
}) {
  const tone = {
    indigo: "border-indigo-200 hover:border-indigo-300",
    sky: "border-sky-200 hover:border-sky-300",
    amber: "border-amber-200 hover:border-amber-300",
    emerald: "border-emerald-200 hover:border-emerald-300",
    rose: "border-rose-200 hover:border-rose-300",
  }[accent || ""] || "hover:border-zinc-300";
  const iconTone = {
    indigo: "text-indigo-600 bg-indigo-50",
    sky: "text-sky-600 bg-sky-50",
    amber: "text-amber-700 bg-amber-50",
    emerald: "text-emerald-700 bg-emerald-50",
    rose: "text-rose-700 bg-rose-50",
  }[accent || ""] || "text-zinc-600 bg-zinc-50";
  const showUsd = usdOverride !== undefined ? usdOverride : usd;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`stat-card p-4 text-left transition-shadow hover:shadow-md ${tone} ${
        highlight ? "ring-1 ring-rose-100" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">
          {label}
        </div>
        <div
          className={`h-7 w-7 rounded-md flex items-center justify-center ${iconTone}`}
        >
          {icon}
        </div>
      </div>
      <div
        className={`font-mono ${highlight ? "text-[20px]" : "text-[18px]"} font-medium mt-1`}
      >
        {points != null
          ? fmtPoints(points)
          : showUsd != null
            ? fmtUSD(showUsd)
            : fmtNPR(npr || 0)}
      </div>
      {npr != null && showUsd != null && (
        <div className="text-[11.5px] font-mono text-zinc-500 mt-0.5">
          {fmtNPR(npr)}
        </div>
      )}
      {sub && (
        <div className="text-[10.5px] font-mono text-zinc-500 mt-0.5">
          {sub}
        </div>
      )}
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-2">
        Click for breakdown →
      </div>
    </button>
  );
}

function Stat({ label, value, accent, span }) {
  const cls = {
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    amber: "text-amber-700",
  }[accent || ""] || "text-zinc-900";
  const colSpan = span === 3 ? "col-span-2 sm:col-span-3" : span === 2 ? "col-span-2" : "";
  return (
    <div className={colSpan}>
      <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
        {label}
      </div>
      <div className={`font-mono text-[14px] font-medium mt-0.5 ${cls}`}>
        {value}
      </div>
    </div>
  );
}
